import { Step } from 'react-joyride';
import { translate } from '../../../i18n';
import { UserRole } from '../../../models/organization/dto/invite-to-organization.dto';
import { OrganizationDto } from '../../../models/organization/dto/organization.dto';

export const COACH_ONBOARDING_TOUR_ID = 'coach-onboarding';

/**
 * A joyride step pinned to the route it lives on. The orchestrator
 * (CoachOnboardingTour) navigates to `route` before showing the step, which is
 * what makes this the one cross-page tour — GuidedTour is single-page by
 * design. `route` is pathname + search relative to the HashRouter root; a step
 * whose target is part of the persistent chrome (sidebar, navbar) still gets a
 * route so the page behind the spotlight is a sensible one.
 */
export type CoachTourStep = Step & { route: string };

/**
 * The coach first-login tutorial (MYS-132): planning → the Coachs tab → their
 * own profile → availabilities → how to replay. Steps 3 and 4 need the
 * caller's own coach id; when it has not resolved (or the coach row is
 * missing) those steps are dropped rather than pointing at a dead route.
 */
export function coachOnboardingSteps(coachId?: string): CoachTourStep[] {
  const steps: (CoachTourStep | null)[] = [
    {
      route: '/admin/calendar',
      target: '[data-tour="coach-planning"]',
      title: translate('walkthrough.coach_tour_planning_title'),
      content: translate('walkthrough.coach_tour_planning_content'),
      disableBeacon: true,
      placement: 'top',
    },
    {
      route: '/admin/calendar',
      target: '[data-tour="sidebar-link/coachs"]',
      title: translate('walkthrough.coach_tour_coachs_link_title'),
      content: translate('walkthrough.coach_tour_coachs_link_content'),
      placement: 'right',
    },
    coachId
      ? {
          route: `/admin/coachDetails?coach=${coachId}`,
          target: '[data-tour="coach-view-toggle"]',
          title: translate('walkthrough.coach_tour_profile_title'),
          content: translate('walkthrough.coach_tour_profile_content'),
          placement: 'bottom',
        }
      : null,
    coachId
      ? {
          route: `/admin/coachDetails?coach=${coachId}&view=calendar`,
          target: '[data-tour="coach-availabilities"]',
          title: translate('walkthrough.coach_tour_availabilities_title'),
          content: translate('walkthrough.coach_tour_availabilities_content'),
          placement: 'top',
        }
      : null,
    {
      route: '/admin/calendar',
      target: '[data-tour="navbar-avatar"]',
      title: translate('walkthrough.coach_tour_replay_title'),
      content: translate('walkthrough.coach_tour_replay_content'),
      placement: 'bottom',
    },
  ];
  return steps.filter((s): s is CoachTourStep => s !== null);
}

/**
 * Should the tour launch by itself right now? Pure so it is testable:
 *
 * - only for a resolved COACH role (never the fail-open ADMIN default);
 * - only when NO organization in the caller's list carries a completion
 *   timestamp — a coach already tutored at another club is not re-tutored
 *   when a second club recruits them (the ticket's "coach déjà présent sur un
 *   autre club" case);
 * - `seenLocally` (the uid-scoped localStorage flag) covers the gap between
 *   finishing the tour and the `my-organizations` refetch landing, plus any
 *   failed completion POST.
 */
export function shouldAutoLaunchCoachTour(args: {
  role: UserRole;
  organizations: OrganizationDto[] | undefined;
  seenLocally: boolean;
}): boolean {
  const { role, organizations, seenLocally } = args;
  if (role !== UserRole.COACH) return false;
  if (seenLocally) return false;
  if (!organizations || organizations.length === 0) return false;
  return !organizations.some((org) => Boolean(org.myCoachTourCompletedAt));
}
